import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import applicationService from '../services/applicationService';
import StatusBadge from '../components/StatusBadge';
import ApplicationStepper from '../components/ApplicationStepper';
import WorkflowSectionContainer from '../components/WorkflowSectionContainer';
import useStepNavigation from '../hooks/useStepNavigation';

const ApplicationDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [application, setApplication] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawalReason, setWithdrawalReason] = useState('');
  const [documentPreview, setDocumentPreview] = useState({
    isOpen: false,
    title: '',
    rawUrl: '',
    previewUrl: '',
    previewType: 'unsupported'
  });

  // Initialize step navigation hook
  const {
    currentStep,
    canAccessStep,
    navigateToStep,
    isStepCompleted
  } = useStepNavigation(application, user?.role);

  useEffect(() => {
    fetchApplicationDetails();
    fetchApplicationHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchApplicationDetails = async () => {
    try {
      const response = await applicationService.getApplicationById(id);
      if (response.success) {
        setApplication(response.data);
        setCandidate(response.data.candidateId);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load application');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicationHistory = async () => {
    try {
      const response = await applicationService.getApplicationHistory(id);
      if (response.success) {
        setHistory(response.data);
      }
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const handleAdvance = async () => {
    if (!window.confirm('Are you sure you want to advance this application to the next stage?')) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await applicationService.advanceApplication(id);
      if (response.success) {
        setApplication(response.data);
        fetchApplicationHistory();
        alert('Application advanced successfully!');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to advance application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim() || rejectReason.length < 10) {
      alert('Please provide a rejection reason (minimum 10 characters)');
      return;
    }

    setActionLoading(true);
    try {
      const response = await applicationService.rejectApplication(id, rejectReason);
      if (response.success) {
        setApplication(response.data);
        fetchApplicationHistory();
        setShowRejectModal(false);
        setRejectReason('');
        alert('Application rejected');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reject application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawalReason.trim() || withdrawalReason.length < 10) {
      alert('Please provide a withdrawal reason (minimum 10 characters)');
      return;
    }

    setActionLoading(true);
    try {
      const response = await applicationService.withdrawApplication(id, withdrawalReason);
      if (response.success) {
        setApplication(response.data);
        fetchApplicationHistory();
        setShowWithdrawModal(false);
        setWithdrawalReason('');
        alert('Application withdrawn successfully');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to withdraw application');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',

    });
  };

  const buildDocumentPreview = (url, filename) => {
    const lowerName = String(filename || '').toLowerCase();
    const lowerUrl = String(url || '').toLowerCase();

    const isPdf = lowerName.endsWith('.pdf') || lowerUrl.includes('.pdf');
    const isImage = ['.png', '.jpg', '.jpeg', '.gif', '.webp'].some(ext => lowerName.endsWith(ext) || lowerUrl.includes(ext));
    const isDoc = lowerName.endsWith('.doc') || lowerName.endsWith('.docx') || lowerUrl.includes('.doc') || lowerUrl.includes('.docx');

    if (isPdf) {
      return { previewType: 'pdf', previewUrl: url };
    }

    if (isImage) {
      return { previewType: 'image', previewUrl: url };
    }

    if (isDoc) {
      return {
        previewType: 'office',
        previewUrl: `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
      };
    }

    return { previewType: 'unsupported', previewUrl: '' };
  };

  const openDocumentPreview = (url, filename) => {
    if (!url) return;

    const preview = buildDocumentPreview(url, filename);

    setDocumentPreview({
      isOpen: true,
      title: filename || 'Document Preview',
      rawUrl: url,
      previewUrl: preview.previewUrl,
      previewType: preview.previewType
    });
  };

  const closeDocumentPreview = () => {
    setDocumentPreview({
      isOpen: false,
      title: '',
      rawUrl: '',
      previewUrl: '',
      previewType: 'unsupported'
    });
  };

  const canAdvance = ['HR', 'ADMIN', 'CHAIRMAN'].includes(user?.role) &&
    application?.currentStage !== 'COMPLETED' &&
    application?.currentStage !== 'REJECTED';

  const canReject = ['HR', 'ADMIN', 'CHAIRMAN'].includes(user?.role) &&
    application?.currentStage !== 'REJECTED' &&
    application?.currentStage !== 'COMPLETED';

  const canWithdraw = user?.role === 'CANDIDATE' &&
    application?.currentStage !== 'REJECTED' &&
    application?.currentStage !== 'WITHDRAWN' &&
    application?.currentStage !== 'COMPLETED';

  // Helper function to check if any documents exist
  const hasAnyDocuments = () => {
    return !!(
      candidate?.documents?.resume?.url ||
      candidate?.resumeUrl ||
      candidate?.documents?.combinedDocuments?.url ||
      (candidate?.additionalDocuments && candidate.additionalDocuments.length > 0)
    );
  };

  const applicationDisplayId = (() => {
    const explicitId = application?.applicationId || application?.referenceId;
    if (explicitId) {
      return String(explicitId).toUpperCase();
    }

    const rawId = application?._id || application?.id || id;
    if (!rawId) return 'N/A';

    // Use the same formatting logic as admin view
    const country = (application?.destinationCountry || '').trim();
    const countryPrefix = country
      ? country.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase().padEnd(2, 'X')
      : 'XX';

    const stableSuffix = rawId.toString().slice(-6).toUpperCase();
    return `${countryPrefix}${stableSuffix}`;
  })();

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background py-4 px-5 max-w-[1000px] mx-auto">
          <div className="flex flex-col items-center justify-center min-h-[300px] gap-3">
            <div className="w-10 h-10 border-4 border-gray-300 border-t-accent-green rounded-full animate-spin"></div>
            <p>Loading application...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !application || !candidate) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background py-4 px-5 max-w-[1000px] mx-auto">
          <div className="flex flex-col items-center justify-center min-h-[300px] gap-3">
            <h2>Error</h2>
            <p>{error || 'Application not found'}</p>
            <button className="py-1.5 px-3 rounded-md font-semibold text-[13px] leading-8 h-fit min-h-0 cursor-pointer transition-all duration-200 border-none whitespace-nowrap inline-flex items-center justify-center align-middle box-border bg-accent-green text-white hover:bg-[#5a6b4d]" onClick={() => navigate(-1)}>
              Go Back
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-background py-4 px-5 max-w-[1000px] mx-auto md:py-3">
        {/* Header with Application Context */}
        <div className="flex justify-between items-center mb-4 gap-4 md:flex-col md:items-stretch">
          <div className="flex items-center gap-4">
            <button className="bg-transparent border-none text-accent-green font-semibold text-[15px] cursor-pointer py-2 px-4 rounded-md transition-all duration-200 hover:bg-[rgba(107,127,92,0.1)]" onClick={() => navigate(-1)}>
              ← Back
            </button>
            <div>
              <h1 className="m-0 text-2xl font-bold text-primary">{application.jobPosting}</h1>
              <div className="flex items-center gap-2 mt-1">
                <StatusBadge stage={application.currentStage} />
                <span className="text-sm text-text-secondary">Applied: {formatDate(application.appliedAt)}</span>
              </div>
              <div className="mt-1 text-sm text-text-secondary">
                Application ID: <span className="text-text-primary font-medium break-all">{applicationDisplayId || 'N/A'}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-3 items-center md:flex-row md:flex-wrap">
            {canWithdraw && (
              <button
                className="py-2 px-4 rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 border-none whitespace-nowrap bg-[#C76B6B] text-white hover:bg-[#B71C1C] disabled:opacity-50 disabled:cursor-not-allowed md:flex-1"
                onClick={() => setShowWithdrawModal(true)}
                disabled={actionLoading}
              >
                Withdraw Application
              </button>
            )}
            {canReject && (
              <button
                className="py-2 px-4 rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 border-none whitespace-nowrap bg-[#C76B6B] text-white hover:bg-[#B71C1C] disabled:opacity-50 disabled:cursor-not-allowed md:flex-1"
                onClick={() => setShowRejectModal(true)}
                disabled={actionLoading}
              >
                Reject
              </button>
            )}
            {canAdvance && (
              <button
                className="py-2 px-4 rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 border-none whitespace-nowrap bg-accent-green text-white hover:bg-[#5a6b4d] disabled:opacity-50 disabled:cursor-not-allowed md:flex-1"
                onClick={handleAdvance}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Advance to Next Stage'}
              </button>
            )}
          </div>
        </div>

        {/* Rejection/Withdrawal Reason Alert */}
        {application.currentStage === 'REJECTED' && application.rejectionReason && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-600 rounded-md">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="m-0 mb-1 text-sm font-semibold text-red-800">Application Rejected</h3>
                <p className="m-0 text-sm text-red-700">{application.rejectionReason}</p>
              </div>
            </div>
          </div>
        )}

        {application.currentStage === 'WITHDRAWN' && application.withdrawalReason && (
          <div className="mb-4 p-4 bg-orange-50 border-l-4 border-orange-600 rounded-md">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="m-0 mb-1 text-sm font-semibold text-orange-800">Application Withdrawn</h3>
                <p className="m-0 text-sm text-orange-700">{application.withdrawalReason}</p>
                {application.withdrawnAt && (
                  <p className="m-0 mt-1 text-xs text-orange-600">Withdrawn on: {formatDate(application.withdrawnAt)}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Full Candidate Profile */}
        <div className="flex flex-col gap-4">
          {/* Candidate Header - Centered Layout */}
          <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            {/* Profile Photo and Name - Centered */}
            <div className="flex flex-col items-center mb-4">
              <div className="flex-shrink-0 mb-3">
                {(() => {
                  const photoUrl = candidate.profilePhoto?.url ||
                    candidate.personalInformation?.profilePhoto?.url ||
                    candidate.photoUrl;

                  return photoUrl ? (
                    <img
                      src={photoUrl}
                      alt={`${candidate.firstName || candidate.personalInformation?.name || ''} ${candidate.lastName || ''}`.trim() || 'Candidate'}
                      className="w-20 h-20 rounded-full object-cover border-2 border-accent-green shadow-md"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const placeholder = e.target.parentNode.querySelector('.placeholder-avatar');
                        if (placeholder) placeholder.style.display = 'flex';
                      }}
                    />
                  ) : null;
                })()}
                <div className="placeholder-avatar w-20 h-20 rounded-full bg-gray-200 border-2 border-accent-green shadow-md flex items-center justify-center"
                  style={{
                    display: (() => {
                      const photoUrl = candidate.profilePhoto?.url ||
                        candidate.personalInformation?.profilePhoto?.url ||
                        candidate.photoUrl;
                      return photoUrl ? 'none' : 'flex';
                    })()
                  }}>
                  <svg className="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <h2 className="m-0 text-lg font-bold text-primary text-center">
                {candidate.personalInformation?.name ||
                  candidate.fullName ||
                  `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() ||
                  'Candidate Name'}
              </h2>
            </div>

            {/* Information Grid - Left and Right */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column - Email and Phone */}
              <div className="space-y-3">
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Email:</span>
                  <span className="text-sm text-text-primary break-all">
                    {candidate.userId?.email || candidate.email || 'Not provided'}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Phone:</span>
                  <span className="text-sm text-text-primary">
                    {candidate.personalInformation?.mobile || candidate.userId?.phoneNumber || candidate.phoneNumber || 'Not provided'}
                  </span>
                </div>
              </div>

              {/* Right Column - Passport and Gender */}
              <div className="space-y-3 ml-4">
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Passport:</span>
                  <span className="text-sm text-text-primary">
                    {candidate.personalInformation?.passportNumber || candidate.passportNumber || 'Not provided'}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Gender:</span>
                  <span className="text-sm text-text-primary">
                    {candidate.personalInformation?.gender || candidate.gender || 'Not provided'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <h3 className="m-0 mb-3 text-base font-semibold text-primary pb-1 border-b border-accent-green">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Date of Birth:</span>
                <span className="text-sm text-text-primary">
                  {candidate.dateOfBirth ? formatDate(candidate.dateOfBirth) : 'Not provided'}
                </span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Nationality:</span>
                <span className="text-sm text-text-primary">
                  {candidate.nationality || 'Not provided'}
                </span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Location:</span>
                <span className="text-sm text-text-primary">
                  {candidate.personalInformation?.location?.state && candidate.personalInformation?.location?.district
                    ? `${candidate.personalInformation.location.district}, ${candidate.personalInformation.location.state}`
                    : candidate.personalInformation?.location?.state || candidate.state || 'Not provided'
                  }
                </span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Address:</span>
                <span className="text-sm text-text-primary">
                  {candidate.address || 'Not provided'}
                </span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Emergency Contact:</span>
                <span className="text-sm text-text-primary">
                  {candidate.emergencyContact || 'Not provided'}
                </span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Passport Expiry:</span>
                <span className="text-sm text-text-primary">
                  {candidate.passportExpiry ? formatDate(candidate.passportExpiry) : 'Not provided'}
                </span>
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <h3 className="m-0 mb-3 text-base font-semibold text-primary pb-1 border-b border-accent-green">Professional Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Current Job Role:</span>
                <span className="text-sm text-text-primary">
                  {candidate.professionalDetails?.currentJobRole || candidate.jobRole || 'Not provided'}
                </span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Work Experience:</span>
                <span className="text-sm text-text-primary">
                  {candidate.professionalDetails?.workExperience || candidate.experience || 'Not provided'}
                </span>
              </div>
              <div className="flex flex-col gap-0.5 md:col-span-2">
                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Education:</span>
                <span className="text-sm text-text-primary">
                  {candidate.professionalDetails?.education || candidate.education || 'Not provided'}
                </span>
              </div>
            </div>
          </div>

          {/* Documents - Always visible */}
          <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <h3 className="m-0 mb-3 text-base font-semibold text-primary pb-1 border-b border-accent-green">Documents</h3>
            {hasAnyDocuments() ? (
              <div className="flex flex-wrap gap-2">
                {(candidate.documents?.resume?.url || candidate.resumeUrl) && (
                  <button
                    type="button"
                    onClick={() => openDocumentPreview(candidate.documents?.resume?.url || candidate.resumeUrl, 'resume.pdf')}
                    className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-accent-green no-underline border border-accent-green rounded text-xs font-semibold transition-all duration-200 hover:bg-accent-green hover:text-white"
                  >
                    Resume/CV
                  </button>
                )}
                {candidate.documents?.combinedDocuments?.url && (
                  <button
                    type="button"
                    onClick={() => openDocumentPreview(candidate.documents.combinedDocuments.url, 'combined-documents.pdf')}
                    className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-accent-green no-underline border border-accent-green rounded text-xs font-semibold transition-all duration-200 hover:bg-accent-green hover:text-white"
                  >
                    Combined Docs
                  </button>
                )}
                {candidate.additionalDocuments && candidate.additionalDocuments.map((doc, index) => (
                  <button
                    type="button"
                    key={index}
                    onClick={() => openDocumentPreview(doc.documentUrl, doc.documentName || `document-${index + 1}.pdf`)}
                    className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-accent-green no-underline border border-accent-green rounded text-xs font-semibold transition-all duration-200 hover:bg-accent-green hover:text-white"
                  >
                    {doc.documentName || `Doc ${index + 1}`}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-secondary">No documents uploaded yet</p>
            )}
          </div>

          {/* Application Questionnaire */}
          {application.questionnaire && application.questionnaire.length > 0 && (
            <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
              <h3 className="m-0 mb-3 text-base font-semibold text-primary pb-1 border-b border-accent-green">Application Questionnaire</h3>
              <div className="space-y-2">
                {application.questionnaire.map((item, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 py-2 border-b border-gray-100 last:border-b-0">
                    <div className="md:col-span-2">
                      <span className="text-sm font-medium text-primary">
                        {item.question}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm text-text-primary">
                        {item.answer}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Application Details */}
          <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <h3 className="m-0 mb-3 text-base font-semibold text-primary pb-1 border-b border-accent-green">Application Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-0.5 md:col-span-2">
                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Application ID:</span>
                <span className="text-sm text-text-primary break-all">{applicationDisplayId || 'N/A'}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Destination Country:</span>
                <span className="text-sm text-text-primary">{application.destinationCountry || 'Not specified'}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Applied Date:</span>
                <span className="text-sm text-text-primary">{formatDate(application.appliedAt)}</span>
              </div>
              {application.completedAt && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Completed Date:</span>
                  <span className="text-sm text-text-primary">{formatDate(application.completedAt)}</span>
                </div>
              )}
              {application.rejectionReason && (
                <div className="flex flex-col gap-0.5 md:col-span-2">
                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Rejection Reason:</span>
                  <span className="text-sm text-error font-medium">
                    {application.rejectionReason}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Application Stepper Navigation */}
          <ApplicationStepper
            currentStep={currentStep}
            applicationStage={application.currentStage}
            onStepClick={navigateToStep}
            canAccessStep={canAccessStep}
            isStepCompleted={isStepCompleted}
          />

          {/* Workflow Section Container - renders only the active section */}
          <WorkflowSectionContainer
            currentStep={currentStep}
            application={application}
            candidate={candidate}
            userRole={user?.role}
            onRefresh={fetchApplicationDetails}
          />

          {history.length > 0 && (
            <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
              <h3 className="m-0 mb-3 text-base font-semibold text-primary pb-1 border-b border-accent-green">Application History</h3>
              <div className="flex flex-col gap-3 mt-3">
                {history.map((record, index) => (
                  <div key={record._id} className="flex gap-3 relative">
                    <div className="w-2 h-2 rounded-full bg-accent-green mt-1.5 flex-shrink-0"></div>
                    {index !== history.length - 1 && (
                      <div className="absolute left-[3px] top-3 w-0.5 h-[calc(100%+12px)] bg-gray-300"></div>
                    )}
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-0.5 gap-2 md:flex-col md:items-start">
                        <span className="font-medium text-primary text-sm">
                          {record.fromStage} → {record.toStage}
                        </span>
                        <span className="text-xs text-text-secondary">
                          {formatDate(record.changedAt)}
                        </span>
                      </div>
                      {record.reason && (
                        <p className="mt-0.5 mb-0 text-xs text-text-secondary">{record.reason}</p>
                      )}
                      {record.notes && (
                        <p className="mt-0.5 mb-0 text-xs text-text-secondary">{record.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {showRejectModal && (
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.5)] flex items-center justify-center z-[1000]" onClick={() => setShowRejectModal(false)}>
            <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto mx-4" onClick={(e) => e.stopPropagation()}>
              <h2 className="m-0 mb-2 text-xl font-bold text-primary">Reject Application</h2>
              <p className="m-0 mb-4 text-text-secondary">Please provide a reason for rejection:</p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason (minimum 10 characters)"
                rows={4}
                minLength={10}
                maxLength={500}
                className="w-full p-3 border border-gray-300 rounded-lg font-[inherit] text-sm resize-vertical mb-4 focus:outline-none focus:border-accent-green"
              />
              <div className="flex gap-3 justify-end">
                <button
                  className="py-1.5 px-3 rounded-md font-semibold text-[13px] leading-8 h-fit min-h-0 cursor-pointer transition-all duration-200 border-none whitespace-nowrap inline-flex items-center justify-center align-middle box-border bg-gray-100 text-text-primary hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  className="py-1.5 px-3 rounded-md font-semibold text-[13px] leading-8 h-fit min-h-0 cursor-pointer transition-all duration-200 border-none whitespace-nowrap inline-flex items-center justify-center align-middle box-border bg-[#C76B6B] text-white hover:bg-[#B71C1C] disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleReject}
                  disabled={actionLoading || rejectReason.length < 10}
                >
                  {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showWithdrawModal && (
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.5)] flex items-center justify-center z-[1000]" onClick={() => setShowWithdrawModal(false)}>
            <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto mx-4" onClick={(e) => e.stopPropagation()}>
              <h2 className="m-0 mb-2 text-xl font-bold text-[#C76B6B]">Withdraw Application</h2>
              <p className="m-0 mb-4 text-text-secondary">Are you sure you want to withdraw this application? This action cannot be undone. Please provide a reason:</p>
              <textarea
                value={withdrawalReason}
                onChange={(e) => setWithdrawalReason(e.target.value)}
                placeholder="Enter withdrawal reason (minimum 10 characters)"
                rows={4}
                minLength={10}
                maxLength={500}
                className="w-full p-3 border border-gray-300 rounded-lg font-[inherit] text-sm resize-vertical mb-4 focus:outline-none focus:border-[#C76B6B]"
              />
              <div className="text-xs text-text-secondary mb-4">
                {withdrawalReason.length}/500 characters (minimum 10 required)
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  className="py-1.5 px-3 rounded-md font-semibold text-[13px] leading-8 h-fit min-h-0 cursor-pointer transition-all duration-200 border-none whitespace-nowrap inline-flex items-center justify-center align-middle box-border bg-gray-100 text-text-primary hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    setShowWithdrawModal(false);
                    setWithdrawalReason('');
                  }}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  className="py-1.5 px-3 rounded-md font-semibold text-[13px] leading-8 h-fit min-h-0 cursor-pointer transition-all duration-200 border-none whitespace-nowrap inline-flex items-center justify-center align-middle box-border bg-[#C76B6B] text-white hover:bg-[#B71C1C] disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleWithdraw}
                  disabled={actionLoading || withdrawalReason.length < 10}
                >
                  {actionLoading ? 'Withdrawing...' : 'Confirm Withdrawal'}
                </button>
              </div>
            </div>
          </div>
        )}

        {documentPreview.isOpen && (
          <div
            className="fixed top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.6)] flex items-center justify-center z-[1100] p-4"
            onClick={closeDocumentPreview}
          >
            <div
              className="bg-white rounded-xl w-full max-w-5xl h-[88vh] shadow-[0_12px_40px_rgba(0,0,0,0.25)] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h3 className="m-0 text-sm font-semibold text-primary truncate pr-4">{documentPreview.title}</h3>
                <div className="flex items-center gap-2">
                  <a
                    href={documentPreview.rawUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-1.5 px-3 rounded-md text-xs font-semibold bg-gray-100 text-primary no-underline hover:bg-gray-200"
                  >
                    Open in New Tab
                  </a>
                  <button
                    type="button"
                    onClick={closeDocumentPreview}
                    className="py-1.5 px-3 rounded-md text-xs font-semibold bg-accent-green text-white hover:bg-[#5a6b4d]"
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="h-[calc(88vh-56px)] bg-gray-50">
                {documentPreview.previewType === 'image' && (
                  <div className="w-full h-full overflow-auto flex items-center justify-center bg-white">
                    <img src={documentPreview.previewUrl} alt={documentPreview.title} className="max-w-full max-h-full object-contain" />
                  </div>
                )}
                {(documentPreview.previewType === 'pdf' || documentPreview.previewType === 'office') && (
                  <iframe
                    src={documentPreview.previewUrl}
                    title={documentPreview.title}
                    className="w-full h-full border-0"
                  />
                )}
                {documentPreview.previewType === 'unsupported' && (
                  <div className="w-full h-full flex items-center justify-center text-center px-6">
                    <div>
                      <p className="text-sm text-text-secondary mb-3">Preview is not available for this file type.</p>
                      <a
                        href={documentPreview.rawUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block py-2 px-4 rounded-md text-xs font-semibold bg-accent-green text-white no-underline hover:bg-[#5a6b4d]"
                      >
                        Open File
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ApplicationDetailsPage;

